import * as angular from 'angular';

import { CompatibilityService } from '@services/compatibilityservice';
import { FixupRelPath, fn, GetVersionInfo } from '@common/utils';
import resolveToError = fn.resolveToError;

import { AppVersion } from '@common/interfaces';
import { NavPageAdminService } from '@common/navdata/navpageadminservice';
import { TNavUrlAccess, NavUrlService, IWebApiRoot, NavClientService } from '@common/navdata';
import { RwfAutoResolver, RwfBackend } from '@common/rwf';
import { RwfSignalRService } from '@common/rwfsignalr';
import { IRoutingService } from '@common/services/autoresolveservice';
import { DialogService } from '@services/dialogservice';
import { InfWindowService } from '@services/infwindowservice';
import { INavCustomization } from '@common/services/settingsservice';
import { NavSettingsService, DefaultCustomizationConfigName } from '@common/services/settingsservice';
import { RwfBootstrap } from '@common/rwf/rwf.bootsrap';

import { KWFApplicationService } from '../KWFApplication';
import { KWFNotificationService } from "../KWFNotificationService";

export * from '@common/services/navurlservice';
export * from '@common/nav';

/** Provider for NavUrl Service: allows attaching Default Nav Url to Service */
const NavUrlServiceProvider = {
    $get: ["CompatibilityService", ($c: CompatibilityService) =>
        new NavUrlService($c, this.NavUrl)],
    /** Default Nav Url  */
    NavUrl: <TNavUrlAccess>undefined
};

const NavClientServiceProvider: ng.IServiceProvider = {
    $get: ["CompatibilityService", ($c: CompatibilityService) =>
        new NavClientService($c, $c.resolve("NavUrl"), $c.resolve("DialogService"))]
};

/**
* - Loading and Saving UserSettings
* - Register Events for (Before) Unload (here as Storing usersettings is the mani reason for this)
*/
const NavSettingsProvider: ng.IServiceProvider =  {
    $get: ["CompatibilityService", "NavCustomization",
    ($c: CompatibilityService, navCustomization: INavCustomization) => new NavSettingsService($c, navCustomization)]
};

angular.module("infoma.nav", ["infoma.angularEx"])
    .filter('navShorten', () => fn.textShorten)
    .constant("WebApiRoot", <IWebApiRoot>{ WebApiRoot: '' })
    .service("CompatibilityService", CompatibilityService)
    .constant("NavCustomization", <INavCustomization>{ ConfigName : DefaultCustomizationConfigName })
    .provider("NavSettings", NavSettingsProvider)
    .provider("NavClient", NavClientServiceProvider)
    .provider("NavUrlService", NavUrlServiceProvider)
    .service("NavPageAdminService", NavPageAdminService)
    .constant("InfomaAppVersion", <AppVersion>{ Version: "default", PatchLevel: "" })
    .config(["$httpProvider", "InfomaAppVersion", "WebApiRoot",
        function httpIntercept($httpProvider: ng.IHttpProvider, infomaAppVersion: AppVersion, webApiRoot: IWebApiRoot) {
            if (webApiRoot && webApiRoot.WithCredentials) {
                $httpProvider.defaults.withCredentials = true;
            }
            var devToken = "" + (window["_InfomaDevToken"] || ""); // Utility, force refresh in DevMode, see mian.ts also
            // Add interceptor to rethrow server caught explicit message (see IErrorDescription)
            $httpProvider.interceptors.push(() => {
                return {
                    request: r => {
                        // Problems with caching (!?!)
                        var url = r.url.toLowerCase();
                        if (infomaAppVersion.Version && (url.lastIndexOf(".html") == url.length - 5)) {
                            var idxFrg = url.indexOf("fragments/");
                            if (idxFrg == 0 || idxFrg == 1) {
                                r.url += "?ver=" + infomaAppVersion.Version + "_" + infomaAppVersion.PatchLevel + devToken;
                            }
                        }

                        if (r.url) {
                            r.url = FixupRelPath(r.url);
                        }

                        return r;
                    },
                    response: r => {
                        return resolveToError(r, true);
                    }
                };
            });
        }])
    .run(["InfomaAppVersion", (appVersion: AppVersion) => { appVersion = GetVersionInfo(appVersion); }]);

declare module 'angular' {
    interface IQService {
        /** angular also resolves Jquery and alike promises, ....  */
        when<T>(p: T | PromiseLike<T>): IPromise<T>;
    }

    interface IPromise<T> {
        /** Without these declarations some of the 'Mixed'
        * Promise Situations lead to tyescript compile errors ...  */
        then<TResult>(successCallback: (promiseValue: T) => TResult | PromiseLike<TResult>, errorCallback?: (reason: any) => any, notifyCallback?: (state: any) => any): IPromise<TResult>;
    }

    namespace auto {
        /** Angular Injector will Implement our Custom Injection  */
        export interface IInjectorService {
            get<TName extends keyof IInjectableServices>(name: TName): IInjectableServices[TName];
        }
    }
}

/** Custom Injectable Services */
export interface IInjectableServices {
    // module 'infoma.navdata'
    'NavClient': NavClientService;
    'WebApiRoot': IWebApiRoot;

    // module 'infoma.angularEx'
    'InfWindowService': InfWindowService;
    'DialogService': DialogService;

    // module 'infoma.nav'
    'NavSettings': NavSettingsService;
    'NavCustomization': INavCustomization;
    'NavUrlService': NavUrlService;
    'CompatibilityService': CompatibilityService;
    'InfomaAppVersion': AppVersion;
    'NavPageAdminService': NavPageAdminService;
    'Routing': IRoutingService;

    //  module 'KWF.Services'
    'RwfBackend': RwfBackend;
    'KWFApplication': KWFApplicationService;
    'KWFNotificationService': KWFNotificationService;
    'RwfSignalRService': RwfSignalRService;
    'RwfBootstrap': RwfBootstrap;
    'RwfRoot': RwfAutoResolver;
}
